import { AvTelemetryMetricAggregate } from '../models/av-telemetry-buffer.model';
import { AvTelemetryEnvelope } from '../models/av-telemetry.model';

/**
 * Collapse raw ticks into one aggregate envelope per window — the first step of shedding.
 *
 * STRUCTURE-PRESERVING: each numeric leaf becomes `{avg,min,max}` in place, so per-stream
 * attribution survives. Flattening by leaf name is what the log summary does, and that trades
 * stream detail away on the grounds that the raw records still hold it — which is false here,
 * because shedding deletes those records.
 *
 * Scalar payload fields are carried forward verbatim: aggregates ship, and the server derives
 * the package from the record's own session id.
 */
export function collapseTicks(
  ticks: readonly AvTelemetryEnvelope[],
  windowMs: number
): AvTelemetryEnvelope[] {
  const windows = new Map<number, AvTelemetryEnvelope[]>();

  ticks.forEach((tick) => {
    const windowStart = Math.floor(tick.occurredAt / windowMs) * windowMs;
    const existing = windows.get(windowStart);

    if (existing) {
      existing.push(tick);
      return;
    }

    windows.set(windowStart, [tick]);
  });

  return Array.from(windows.entries())
    .sort(([a], [b]) => a - b)
    .map(([windowStart, batch]) => buildAggregate(windowStart, windowMs, batch));
}

function buildAggregate(
  windowStart: number,
  windowMs: number,
  batch: readonly AvTelemetryEnvelope[]
): AvTelemetryEnvelope {
  const first = batch[0];

  return {
    type: 'tickAggregate',
    schemaVersion: first.schemaVersion,
    occurredAt: windowStart,
    vendor: first.vendor,
    vendorVersion: first.vendorVersion,
    // Earliest ordinal in the window, so aggregates sort where their ticks were.
    seq: Math.min(...batch.map((tick) => tick.seq)),
    payload: {
      ...aggregateNode(batch.map((tick) => tick.payload)),
      aggregateWindowMs: windowMs,
      tickCount: batch.length,
    },
  };
}

/**
 * Merges the same position across every tick in the window: numbers become aggregates, nested
 * records recurse, scalars carry forward from the first tick that has one.
 */
function aggregateNode(nodes: readonly unknown[]): Record<string, unknown> {
  const records = nodes.filter(isPlainRecord);
  const merged: Record<string, unknown> = {};

  new Set(records.flatMap((node) => Object.keys(node))).forEach((key) => {
    const values = records.map((node) => node[key]).filter((value) => value !== undefined);

    const numbers = values.filter(
      (value): value is number => typeof value === 'number' && Number.isFinite(value)
    );
    if (numbers.length) {
      merged[key] = aggregate(numbers);
      return;
    }

    if (values.some(isPlainRecord)) {
      merged[key] = aggregateNode(values);
      return;
    }

    const scalar = values.find(
      (value) => typeof value === 'string' || typeof value === 'boolean'
    );
    if (scalar !== undefined) {
      merged[key] = scalar;
    }
  });

  return merged;
}

function aggregate(values: readonly number[]): AvTelemetryMetricAggregate {
  return {
    avg: values.reduce((sum, value) => sum + value, 0) / values.length,
    min: Math.min(...values),
    max: Math.max(...values),
  };
}

/** Arrays are excluded: their indices are not metric names. */
function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
